// ./src/utils/api/TcoService.ts
import { gql } from "@apollo/client";
import { ApiService } from "./ApiService";
import { OpResult } from "@sdflc/api-helpers";

// ===========================================================================
// Fragments
// ===========================================================================

const TCO_CATEGORY_BREAKDOWN_FRAGMENT = gql`
  fragment TcoCategoryBreakdownFields on TcoCategoryBreakdown {
    categoryCode
    categoryName
    totalCost
    recordsCount
    sharePercent
    perMonth
    perDistance
    projectedAnnualCost
  }
`;

const TCO_MONTHLY_POINT_FRAGMENT = gql`
  fragment TcoMonthlyPointFields on TcoMonthlyPoint {
    year
    month
    totalCost
    refuelsCost
    expensesCost
  }
`;

/**
 * Distance stats block — shared structure for both user-unit and car-unit blocks.
 * Inline fragment used instead of a named fragment because TcoDistanceStats
 * is used in two fields (distanceByUserUnit, distanceByCarUnit) on the same
 * parent type, which would cause a fragment name collision.
 */
const TCO_DISTANCE_STATS_FIELDS = `
  drivenDistance
  avgDistancePerMonth
  avgDistancePerYear
  unit
`;

const REPORT_TCO_GET_QUERY = gql`
  ${TCO_CATEGORY_BREAKDOWN_FRAGMENT}
  ${TCO_MONTHLY_POINT_FRAGMENT}
  query ReportTcoGet($params: TcoReportParamsInput) {
    reportTcoGet(params: $params) {
      code
      data {
        carId
        ownershipStartAt
        monthsOwned

        # Monetary totals
        totalCost
        totalRefuelsCost
        totalExpensesCost

        # Record counts
        totalRefuelsCount
        totalExpensesCount

        # Per-unit averages
        perMonth
        perDistance

        # Projected annual cost (perMonth × 12)
        projectedAnnualCost

        # Distance stats in user's preferred unit (always present)
        distanceByUserUnit {
          ${TCO_DISTANCE_STATS_FIELDS}
        }

        # Distance stats in car's odometer unit (null when same as user unit)
        distanceByCarUnit {
          ${TCO_DISTANCE_STATS_FIELDS}
        }

        # Fuel volume stats in user's preferred volume unit
        volumeStats {
          totalVolume
          avgVolumePerMonth
          avgVolumePerYear
          unit
        }

        # Depreciation (null unless purchasePrice + currentCarValue both available)
        depreciationTotal
        depreciationPerMonth
        depreciationPerYear

        categories {
          ...TcoCategoryBreakdownFields
        }
        monthlyTrend {
          ...TcoMonthlyPointFields
        }
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

// ===========================================================================
// Types
// ===========================================================================

/**
 * Distance metrics grouped with a single unit label.
 * Used for both user-preferred unit and car odometer unit blocks.
 */
export interface TcoDistanceStats {
  /** Total distance driven since ownership start */
  drivenDistance?: number | undefined;
  /** Average distance driven per calendar month */
  avgDistancePerMonth?: number | undefined;
  /** Average distance driven per year (avgDistancePerMonth × 12) */
  avgDistancePerYear?: number | undefined;
  /** Unit for all values in this block: 'km' or 'mi' */
  unit: string;
}

/**
 * Fuel volume metrics grouped with a single unit label.
 * Always expressed in the user's volumeIn preference.
 */
export interface TcoVolumeStats {
  /** Total fuel volume purchased since ownership start */
  totalVolume?: number | undefined;
  /** Average fuel volume purchased per calendar month */
  avgVolumePerMonth?: number | undefined;
  /** Average fuel volume purchased per year (avgVolumePerMonth × 12) */
  avgVolumePerYear?: number | undefined;
  /** Unit for all values in this block: 'l', 'gal-us', or 'gal-uk' */
  unit: string;
}

/**
 * Cost breakdown for a single expense category within the TCO report.
 * Monetary values are in the user's home currency.
 * perDistance is in the user's preferred distance unit.
 */
export interface TcoCategoryBreakdown {
  /** Expense category code (e.g. FUEL, MAINTENANCE, INSURANCE) */
  categoryCode: string;
  /** Localized category name (null if translation missing) */
  categoryName?: string | undefined;
  /** Total amount spent in this category in home currency */
  totalCost?: number | undefined;
  /** Total number of expense/refuel records in this category */
  recordsCount?: number | undefined;
  /** This category's share of total TCO cost, 0–100 */
  sharePercent?: number | undefined;
  /** Average cost per calendar month since ownership start */
  perMonth?: number | undefined;
  /** Average cost per distance unit driven */
  perDistance?: number | undefined;
  /** Projected cost for the next 12 months based on perMonth (perMonth × 12) */
  projectedAnnualCost?: number | undefined;
}

/**
 * A single month's cost data point for the TCO trend chart.
 * Monetary values are in the user's home currency.
 */
export interface TcoMonthlyPoint {
  year: number;
  month: number;
  /** Total cost for the month: refuels + all expenses */
  totalCost?: number | undefined;
  /** Refuel cost portion */
  refuelsCost?: number | undefined;
  /** Expense cost portion */
  expensesCost?: number | undefined;
}

/**
 * Full True Cost of Ownership report for a single car.
 * All monetary values are in the user's home currency.
 * perDistance values use the user's distanceIn preference.
 */
export interface TcoReport {
  /** The car this report covers */
  carId: string;
  /** The date used as ownership baseline (ISO UTC) */
  ownershipStartAt?: string | undefined;
  /** Full calendar months from ownershipStartAt to now (minimum 1) */
  monthsOwned: number;

  /** Total cost: all refuels + all expenses in home currency */
  totalCost?: number | undefined;
  /** Refuel cost component of totalCost */
  totalRefuelsCost?: number | undefined;
  /** Non-refuel expense component of totalCost */
  totalExpensesCost?: number | undefined;

  /** Total number of refuel records */
  totalRefuelsCount?: number | undefined;
  /** Total number of expense records across all categories */
  totalExpensesCount?: number | undefined;

  /** Average total cost per calendar month */
  perMonth?: number | undefined;
  /** Average total cost per distance unit driven */
  perDistance?: number | undefined;
  /** Projected total cost for the next 12 months (perMonth × 12) */
  projectedAnnualCost?: number | undefined;

  /**
   * Distance stats in user's preferred unit (distanceIn from profile).
   * Always present when there is driven distance data.
   */
  distanceByUserUnit?: TcoDistanceStats | undefined;
  /**
   * Distance stats in car's odometer unit (mileageIn).
   * Null/undefined when car unit equals user unit — frontend should only
   * render this block when it is present.
   */
  distanceByCarUnit?: TcoDistanceStats | undefined;

  /** Fuel volume stats in user's preferred volume unit */
  volumeStats?: TcoVolumeStats | undefined;

  /** Total depreciation: purchasePrice - currentCarValue (null if data unavailable) */
  depreciationTotal?: number | undefined;
  /** Average depreciation per calendar month */
  depreciationPerMonth?: number | undefined;
  /** Projected annual depreciation (depreciationPerMonth × 12) */
  depreciationPerYear?: number | undefined;

  /** Breakdown of costs by expense category, sorted by share descending */
  categories: TcoCategoryBreakdown[];
  /** Monthly cost trend, ordered oldest-first */
  monthlyTrend: TcoMonthlyPoint[];
}

/**
 * Per-car current market value supplied by the user.
 * Used to calculate depreciation (purchasePrice - currentCarValue).
 * Values must be in the user's home currency.
 */
export interface TcoCarValueInput {
  /** The car to assign this value to */
  carId: string;
  /** Current market value in the user's home currency */
  value: number;
}

export interface TcoReportParamsInput {
  /** Car IDs to include. Empty or omitted = all accessible cars */
  carIds?: string[] | undefined;
  /** User's timezone offset in minutes from UTC (like JS getTimezoneOffset()) */
  timezoneOffset?: number | undefined;
  /** Language code for category name localization (default: en) */
  lang?: string | undefined;
  /** Number of months to include in the monthly trend (default: 24) */
  trendMonths?: number | undefined;
  /**
   * Current market values per car for depreciation calculation.
   * If a car is not listed here, its depreciation fields will be undefined.
   */
  currentCarValues?: TcoCarValueInput[] | undefined;
}

// ===========================================================================
// Service
// ===========================================================================

/**
 * TCO service for the True Cost of Ownership report.
 * Returns lifetime cost totals, per-category breakdowns, distance and volume
 * stats, optional depreciation, and a monthly trend for each requested car.
 */
export class TcoService {
  /**
   * Get TCO report for all accessible cars (or specific cars if carIds provided).
   */
  static async get(params?: TcoReportParamsInput): Promise<OpResult> {
    const result = await ApiService.executeQuery(REPORT_TCO_GET_QUERY, {
      params: params || {},
    });
    return result;
  }
}